import { yupResolver } from '@hookform/resolvers/yup'
import { forEach, map } from 'lodash-es'
import { FC, useCallback, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PAYMENT_PROVIDER } from './GroupsTab'
import Loader from '../../../common/components/Loader'
import {
  createPaymentProvider,
  deletePaymentProvider,
  getPaymentProviders,
  updatePaymentProvider,
} from '../../../core/services/groups'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormGroup } from '@/components/ui/form-group'
import { Card, CardContent } from '@/components/ui/card'
import SelectField from '@/components/select-field'

export interface IPaymentProviderValues {
  paymentProviders: {
    id?: string
    title: string
    paymentProvider?: PAYMENT_PROVIDER
    apiSecret?: string
    username?: string
    password?: string
    verificationKey?: string
    saved?: boolean
    backendId?: string
  }[]
}

export const PaymentProvidersTab: FC = ({}): JSX.Element => {
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false)
  const [deleteIndex, setDeleteIndex] = useState<number | undefined>()

  const paymentProviders = [
    { value: 'BT_IPAY', label: 'Bank Transfer' },
    { value: 'STRIPE', label: 'Stripe' },
  ]

  const schema = yup.object().shape({
    paymentProviders: yup
      .array()
      .of(
        yup.object().shape({
          backendId: yup.string().optional(),
          id: yup.string().optional(),
          title: yup.string().required('Title id is required'),
          paymentProvider: yup
            .string()
            .oneOf(['BT_IPAY', 'STRIPE'])
            .required('Payment provider is required'),
          apiSecret: yup
            .string()
            .optional()
            .when(['paymentProvider', 'saved'], {
              is: ([val, saved]: [string, boolean]) =>
                saved && val === 'STRIPE',
              then: (schema) => schema.required('Api secret is required'),
              otherwise: (schema) => schema.optional().nullable(),
            }),
          username: yup
            .string()
            .optional()
            .when('paymentProvider', {
              is: (val: string) => val === 'BT_IPAY',
              then: (schema) => schema.required('Username secret is required'),
              otherwise: (schema) => schema.optional().nullable(),
            }),
          password: yup
            .string()
            .optional()
            .when('paymentProvider', {
              is: ([val, saved]: [string, boolean]) =>
                saved && val === 'BT_IPAY',
              then: (schema) => schema.required('Password secret is required'),
              otherwise: (schema) => schema.optional().nullable(),
            }),
          verificationKey: yup
            .string()
            .optional()
            .when('paymentProvider', {
              is: ([val, saved]: [string, boolean]) =>
                saved && val === 'BT_IPAY',
              then: (schema) => schema.required('Verification Key is required'),
              otherwise: (schema) => schema.optional().nullable(),
            }),
          saved: yup.boolean().optional(),
        }),
      )
      .required(),
  })

  const {
    register,
    control,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<IPaymentProviderValues>({
    mode: 'onChange',
    resolver: yupResolver<IPaymentProviderValues>(schema),
  })

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const { data, status } = await getPaymentProviders()

      if (status === 200) {
        setValue('paymentProviders', [])
        forEach(data, (paymentProvider) => {
          append({
            backendId: paymentProvider.id,
            id: paymentProvider.id,
            title: paymentProvider.title,
            paymentProvider: paymentProvider.name,
            apiSecret: paymentProvider.secret,
            username: paymentProvider.username,
            password: paymentProvider.password,
            verificationKey: paymentProvider.verificationKey,
            saved: true,
          })
        })
      }
    } catch (err) {
      if (err instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(err.message)
      } else {
        console.error(err)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'paymentProviders',
  })

  const savePaymentProvider = async (index: number) => {
    const isValid = await trigger(`paymentProviders.${index}`)
    if (!isValid) return
    const paymentProvider = watch(`paymentProviders.${index}`)

    if (paymentProvider.saved) {
    }

    try {
      setLoading(true)
      const { status } = paymentProvider.saved
        ? await updatePaymentProvider(paymentProvider.id as string, {
            name: paymentProvider.paymentProvider as PAYMENT_PROVIDER,
            password: paymentProvider.password as string,
            secret: paymentProvider.apiSecret as string,
            title: paymentProvider.title,
            username: paymentProvider.username as string,
            verificationKey: paymentProvider.verificationKey as string,
          })
        : await createPaymentProvider({
            name: paymentProvider.paymentProvider as PAYMENT_PROVIDER,
            password: paymentProvider.password as string,
            secret: paymentProvider.apiSecret as string,
            title: paymentProvider.title,
            username: paymentProvider.username as string,
            verificationKey: paymentProvider.verificationKey as string,
          })
      if (status === 200) {
        getData()
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const deleteProvider = async (id: string) => {
    try {
      setLoading(true)
      const { status } = await deletePaymentProvider(id)
      if (status === 204) {
        getData()
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {loading && <Loader />}
      <form className="space-y-7">
        {map(fields, (field, index) => {
          const paymentProvider = watch(
            `paymentProviders.${index}.paymentProvider`,
          )
          return (
            <Card key={field.id} className="p-4 space-y-7">
              <FormGroup
                label="Payment Provider"
                error={
                  errors?.paymentProviders?.[index]?.paymentProvider?.message
                }
              >
                <div className="flex w-full gap-2 items-center justify-between">
                  <Controller
                    name={`paymentProviders.${index}.paymentProvider`}
                    control={control}
                    render={({ field }) => (
                      <SelectField
                        outline
                        placeholder="Select Payment Provider"
                        options={paymentProviders.map((provider) => ({
                          value: provider.value,
                          label: provider.label,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        error={
                          errors?.paymentProviders?.[index]?.paymentProvider
                            ?.message
                        }
                        wrapperClassName="flex-1"
                      />
                    )}
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setDeleteIndex(index)
                      setDeleteConfirmation(true)
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </FormGroup>
              <FormGroup
                label="Title"
                error={errors?.paymentProviders?.[index]?.title?.message}
                htmlFor={`title-${index}`}
              >
                <Input
                  id={`title-${index}`}
                  isInvalid={
                    !!errors?.paymentProviders?.[index]?.title?.message
                  }
                  {...register(`paymentProviders.${index}.title`)}
                  type="text"
                  placeholder="Title"
                />
              </FormGroup>
              {paymentProvider === 'STRIPE' && (
                <FormGroup
                  label="API Secret"
                  error={errors?.paymentProviders?.[index]?.apiSecret?.message}
                  htmlFor={`apiSecret-${index}`}
                >
                  <Input
                    id={`apiSecret-${index}`}
                    isInvalid={
                      !!errors?.paymentProviders?.[index]?.apiSecret?.message
                    }
                    {...register(`paymentProviders.${index}.apiSecret`)}
                    type="text"
                    placeholder="API Secret"
                  />
                </FormGroup>
              )}
              {paymentProvider === 'BT_IPAY' && (
                <>
                  <div className="flex gap-4">
                    <FormGroup
                      label="Username"
                      error={
                        errors?.paymentProviders?.[index]?.username?.message
                      }
                      htmlFor={`username-${index}`}
                      className="flex-1"
                    >
                      <Input
                        id={`username-${index}`}
                        isInvalid={
                          !!errors?.paymentProviders?.[index]?.username?.message
                        }
                        {...register(`paymentProviders.${index}.username`)}
                        type="text"
                        placeholder="Enter Username"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Password"
                      error={
                        errors?.paymentProviders?.[index]?.password?.message
                      }
                      htmlFor={`password-${index}`}
                      className="flex-1"
                    >
                      <Input
                        id={`password-${index}`}
                        isInvalid={
                          !!errors?.paymentProviders?.[index]?.password?.message
                        }
                        {...register(`paymentProviders.${index}.password`)}
                        type="text"
                        placeholder="Enter Password"
                      />
                    </FormGroup>
                  </div>
                  <FormGroup
                    label="Verification Key"
                    error={
                      errors?.paymentProviders?.[index]?.verificationKey
                        ?.message
                    }
                    htmlFor={`verification-key-${index}`}
                  >
                    <Input
                      id={`verification-key-${index}`}
                      isInvalid={
                        !!errors?.paymentProviders?.[index]?.verificationKey
                          ?.message
                      }
                      {...register(`paymentProviders.${index}.verificationKey`)}
                      type="text"
                      placeholder="Verification Key"
                    />
                  </FormGroup>
                </>
              )}
              <div className="flex gap-2 justify-end mt-3">
                <Button variant="secondary" size="sm" type="button">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  size="sm"
                  onClick={() => {
                    savePaymentProvider(index)
                  }}
                >
                  Save
                </Button>
              </div>
            </Card>
          )
        })}
      </form>
      <Button
        className="w-full mt-4"
        variant="secondary"
        onClick={() => {
          append({
            title: '',
            apiSecret: '',
            username: '',
            password: '',
            verificationKey: '',
            saved: false,
          })
        }}
      >
        Add New Payment Provider
      </Button>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteConfirmation}
        setDeleteConfirmationModal={setDeleteConfirmation}
        deleteTitle={'Payment Provider'}
        deleteFunc={() => {
          const backendId = watch(
            `paymentProviders.${deleteIndex as number}.backendId`,
          )
          if (backendId) {
            deleteProvider(backendId as string)
          } else {
            remove(deleteIndex)
          }
        }}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
